// Margin Account application -- the one step /margin adds to the wizard.
//
// Step 7. Everything before it is the ordinary individual form, filled in again as
// /syariah does. Step 5, the Regular Account's bank and RDN, is skipped: the
// account already has its RDN, and going back through that step would let the
// client overwrite it. The bank for the Margin RDN is chosen here instead.
//
// What this step asks for follows from what the eligibility check said:
//   always                    the RDN bank for the Margin Account
//   account under 6 months    the transaction history from another broker
//   married                   spouse's name, spouse's KTP, KK / marriage certificate / marriage book
//
// This only gathers. The server decides whether the application is accepted when it
// is finally submitted (controllers/margin.js), so a missing document here is a
// convenience to the client and not the check.
//
// Documents are uploaded one at a time to /api/margin/upload and are not part of
// the Formik values. They are PDF, JPG or PNG, which is why this does not use the
// shared file input: that one resizes images and cannot carry a PDF.

import React, { Component, Fragment } from 'react';
import { Form } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { RootContext } from '../Home';
import CustomInput from '../components/mini/CustomInput';
import ButtonCustom from '../components/mini/ButtonCustom';
import { plainMessage, isMarried, hasPicked, MARGIN_BANK_LABELS } from '../lib/marginHelpers';

const link_api = (window.location.host === 'openaccount.nhsec.co.id')
    ? process.env.REACT_APP_BASE_URL_PROD
    : process.env.REACT_APP_BASE_URL_DEV;

const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

class MarginInformationForm extends Component {
    state = {
        // kind -> true once the server has the file
        uploaded: { broker_history: false, spouse_ktp: false, marriage_doc: false },
        // kind -> true while a file is on its way
        busy: {},
        // kind -> text to show under that slot
        slotError: {},
        submitting: false,
        errorMessage: '',
    };

    componentDidMount() {
        this._mounted = true;
        // What the eligibility check already found on the server, so a client who
        // came back to this step does not appear to have uploaded nothing.
        const margin = this.props.store && this.props.store.state.margin;
        if (margin && margin.uploaded) this.setState({ uploaded: Object.assign({}, this.state.uploaded, margin.uploaded) });
    }

    componentWillUnmount() { this._mounted = false; }

    store = () => this.props.store;

    memberId = () => this.store().state.member_id || this.store().state.registration.id;

    t = (key) => this.store().dispatch({ type: 'translate', text: key });

    upload = async (kind, file) => {
        if (!file) return;
        const lang = this.store().state.lang;
        const setSlot = (patch) => this.setState((s) => ({
            busy: Object.assign({}, s.busy, patch.busy || {}),
            slotError: Object.assign({}, s.slotError, patch.slotError || {}),
            uploaded: Object.assign({}, s.uploaded, patch.uploaded || {}),
        }));

        // The server checks all of this again, from the file's own bytes. This is so the
        // client finds out before waiting on an upload that cannot succeed.
        if (file.size > MAX_BYTES) { setSlot({ slotError: { [kind]: lang === 'id' ? 'Ukuran file maksimal 10 MB.' : 'The file must be 10 MB or smaller.' } }); return; }
        if (file.type && ALLOWED_TYPES.indexOf(file.type) === -1) { setSlot({ slotError: { [kind]: lang === 'id' ? 'File harus berupa PDF, JPG, atau PNG.' : 'The file must be a PDF, JPG or PNG.' } }); return; }

        setSlot({ busy: { [kind]: true }, slotError: { [kind]: '' } });
        try {
            const form = new FormData();
            form.append('member_id', this.memberId());
            form.append('nh_auth', this.store().state.registration.auth_token);
            form.append('kind', kind);
            form.append('file', file);
            const res = await axios.post(link_api + '/api/margin/upload', form, { timeout: 180000 });
            if (res.data && res.data.status) {
                setSlot({ busy: { [kind]: false }, uploaded: { [kind]: true } });
            } else {
                setSlot({ busy: { [kind]: false }, slotError: { [kind]: plainMessage(res.data && res.data.message, lang) } });
            }
        } catch (e) {
            // A line that dropped, or a timeout. Nothing was recorded, so say so and let them choose again.
            setSlot({ busy: { [kind]: false }, slotError: { [kind]: this.t('margin_network_error') || plainMessage({ id: 'Koneksi terputus. Silakan coba lagi.', en: 'The connection dropped. Please try again.' }, lang) } });
        }
    };

    handleSubmit = (store) => async (values) => {
        this.setState({ submitting: true, errorMessage: '' });
        try {
            const memberId = this.memberId();
            if (!memberId) throw new Error('member id missing');
            const bank = (values.rdn_bank_margin && typeof values.rdn_bank_margin === 'object') ? values.rdn_bank_margin.value : values.rdn_bank_margin;
            const married = isMarried(store.state.registration.marital_status, store.state.master.master_marital_status);

            const data = { margin_stat: true, rdn_bank_margin: bank };
            if (married) data.spouse_name = (values.spouse_name || '').trim();

            const param = new URLSearchParams({
                member_id: memberId,
                nh_auth: store.state.registration.auth_token,
                data: JSON.stringify(data),
            });
            const res = await axios.post(link_api + '/api/member', param.toString(), {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                timeout: 60000,
            });
            if (!res.data || !res.data.status) {
                this.setState({ errorMessage: plainMessage(res.data && res.data.message, store.state.lang) });
                return;
            }
            // Home moves on to the summary, where the final submit is made.
            store.dispatch({ type: 'handleSubmit', field: 'registration_margin_completed', values: { rdn_bank_margin: bank, spouse_name: data.spouse_name } });
        } catch (e) {
            this.setState({ errorMessage: this.t('margin_network_error') || 'The connection dropped. Please try again.' });
        } finally {
            if (this._mounted) this.setState({ submitting: false });
        }
    };

    renderSlot(kind, label, hint) {
        const done = this.state.uploaded[kind];
        const busy = !!this.state.busy[kind];
        const err = this.state.slotError[kind];
        const id = 'margin_file_' + kind;
        return (
            <div key={kind} className="margin-doc-slot" style={{ border: '1px solid #e0e0e0', borderRadius: 6, padding: 12, marginBottom: 12 }}>
                <div style={{ fontWeight: 600 }}>{label} <span style={{ color: '#c00' }}>*</span></div>
                {hint ? <div style={{ fontSize: '0.85em', color: '#666', margin: '4px 0 8px' }}>{hint}</div> : null}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <label htmlFor={id} className="btn btn-outline-primary btn-sm" style={{ margin: 0, opacity: busy ? 0.6 : 1 }}>
                        {busy ? this.t('margin_uploading') : (done ? this.t('margin_replace_file') : this.t('btn_choosefile'))}
                    </label>
                    <input
                        id={id}
                        data-testid={id}
                        type="file"
                        accept={ACCEPT}
                        disabled={busy}
                        style={{ display: 'none' }}
                        onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; this.upload(kind, f); }}
                    />
                    <span data-testid={id + '_status'} style={{ color: done ? '#1a7f37' : '#777', fontSize: '0.9em' }}>
                        {done ? this.t('margin_uploaded') : this.t('margin_not_uploaded')}
                    </span>
                </div>
                {err ? <div data-testid={id + '_error'} style={{ color: '#c00', fontSize: '0.85em', marginTop: 6 }}>{err}</div> : null}
            </div>
        );
    }

    render() {
        const store = this.props.store;
        const margin = store.state.margin || {};
        const proofRequired = !!margin.proofRequired;
        const married = isMarried(store.state.registration.marital_status, store.state.master.master_marital_status);
        const banks = (margin.banks && margin.banks.length ? margin.banks : Object.keys(MARGIN_BANK_LABELS))
            .map((b) => ({ value: b, label: MARGIN_BANK_LABELS[b] || b.toUpperCase() }));

        const docsComplete =
            (!proofRequired || this.state.uploaded.broker_history) &&
            (!married || (this.state.uploaded.spouse_ktp && this.state.uploaded.marriage_doc));
        const anyBusy = Object.keys(this.state.busy).some((k) => this.state.busy[k]);

        return (
            <Fragment>
                <Formik
                    enableReinitialize
                    initialValues={{
                        // The select wants an option object, not the stored text. Home keeps what
                        // the server last saved, as text.
                        rdn_bank_margin: (typeof store.state.registration.rdn_bank_margin === 'object' && store.state.registration.rdn_bank_margin)
                            ? store.state.registration.rdn_bank_margin
                            : (banks.find((b) => b.value === store.state.registration.rdn_bank_margin) || ''),
                        spouse_name: store.state.registration.spouse_name || '',
                    }}
                    validationSchema={Yup.object().shape({
                        // Formik turns '' into undefined before validating, so required() would stop
                        // an untouched select. It would not stop an option object whose value is
                        // empty, which hasPicked does.
                        rdn_bank_margin: Yup.mixed().test('picked', 'error_valueMissing', hasPicked),
                        spouse_name: married ? Yup.string().trim().required('error_valueMissing') : Yup.string().nullable(),
                    })}
                    onSubmit={this.handleSubmit(store)}
                >
                    {({ values, errors, touched, handleSubmit, setFieldValue, setFieldTouched }) => (
                        <Form id="margin-information" onSubmit={handleSubmit}>
                            <h3>{this.t('margin_step_title')}</h3>
                            <p>{this.t('margin_step_desc')}</p>

                            <Form.Row>
                                <CustomInput
                                    col="12"
                                    labelName={this.t('margin_rdn_bank')}
                                    name="rdn_bank_margin"
                                    type="select"
                                    required
                                    value={values.rdn_bank_margin}
                                    onChange={(name, value) => setFieldValue(name, value)}
                                    onBlur={setFieldTouched}
                                    placeholder={this.t('placeholder_select')}
                                    error={errors.rdn_bank_margin ? this.t('error_valueMissing') : ''}
                                    touched={touched.rdn_bank_margin}
                                    option={banks}
                                />
                            </Form.Row>
                            <div style={{ fontSize: '0.85em', color: '#666', marginBottom: 16 }}>{this.t('margin_rdn_bank_hint')}</div>

                            {(proofRequired || married) ? (
                                <fieldset style={{ marginTop: 8 }}>
                                    <legend style={{ fontSize: '1.05em' }}>{this.t('margin_docs_title')}</legend>
                                    <div style={{ fontSize: '0.85em', color: '#666', marginBottom: 10 }}>{this.t('margin_docs_hint')}</div>

                                    {proofRequired ? (
                                        <Fragment>
                                            <div data-testid="margin_proof_notice" style={{ background: '#fff7e0', border: '1px solid #f0d58a', borderRadius: 6, padding: 10, marginBottom: 12, fontSize: '0.9em' }}>
                                                {this.t('margin_proof_required_notice')}
                                            </div>
                                            {this.renderSlot('broker_history', this.t('margin_doc_broker_history'))}
                                        </Fragment>
                                    ) : null}

                                    {married ? (
                                        <Fragment>
                                            <div data-testid="margin_married_notice" style={{ fontSize: '0.9em', margin: '4px 0 10px' }}>{this.t('margin_married_notice')}</div>
                                            <Form.Row>
                                                <Form.Group className="col-12">
                                                    <Form.Label>{this.t('margin_spouse_name')} <span style={{ color: '#c00' }}>*</span></Form.Label>
                                                    <Form.Control
                                                        className="form-input"
                                                        name="spouse_name"
                                                        data-testid="margin_spouse_name"
                                                        maxLength="100"
                                                        value={values.spouse_name}
                                                        onChange={(e) => setFieldValue('spouse_name', e.target.value)}
                                                        onBlur={() => setFieldTouched('spouse_name', true)}
                                                        isInvalid={!!(touched.spouse_name && errors.spouse_name)}
                                                    />
                                                    <Form.Control.Feedback type="invalid">{this.t('error_valueMissing')}</Form.Control.Feedback>
                                                </Form.Group>
                                            </Form.Row>
                                            {this.renderSlot('spouse_ktp', this.t('margin_doc_spouse_ktp'))}
                                            {this.renderSlot('marriage_doc', this.t('margin_doc_marriage'))}
                                        </Fragment>
                                    ) : null}
                                </fieldset>
                            ) : null}

                            {this.state.errorMessage ? (
                                <div data-testid="margin_error" style={{ color: '#c00', margin: '12px 0' }}>{this.state.errorMessage}</div>
                            ) : null}

                            <div style={{ marginTop: 30, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                                <ButtonCustom
                                    class="yellow-btn"
                                    value={this.t('btn_prev')}
                                    onButtonClick={() => store.dispatch({ type: 'clickButton', field: 'prev' })}
                                    disabled={this.state.submitting}
                                />
                                <ButtonCustom
                                    id="margin-continue-btn"
                                    type="submit"
                                    class="blue-btn"
                                    value={this.t('btn_next')}
                                    disabled={this.state.submitting || anyBusy || !docsComplete}
                                />
                            </div>
                        </Form>
                    )}
                </Formik>
            </Fragment>
        );
    }
}

/** The wizard hands over its store through context; this hands it to the class. */
export default function MarginInformation() {
    return (
        <RootContext.Consumer>
            {(store) => <MarginInformationForm store={store} />}
        </RootContext.Consumer>
    );
}

export { MarginInformationForm };
export { plainMessage, isMarried };
